import {useCallback, useEffect, useMemo, useState} from "react";
import {
	App as AntdApp,
	Card,
	Input,
	Select,
	Space,
	Switch,
	Table,
	Typography
} from "antd";

import {listFieldRegistry, updateFieldRegistry} from "@modules/fields/api/fieldRegistryApi.js";
import {fetchBannerFields, saveBannerContextMenuFields} from "../api/settingsApi.js";

const {Text} = Typography;
const {TextArea} = Input;

const XML_EXPORT_TYPE_OPTIONS = [
	{label: "Tag annidato", value: "element"},
	{label: "Attributo di gabbia", value: "attribute"}
];

// Ordine fisso di visualizzazione: solo questi 2 campi, mai un catalogo aperto
// (niente AI, niente aggiunta/eliminazione — a differenza del tab Descrizione AI gabbie).
const BANNER_FIELD_ORDER = ["banner_collegato", "categoria"];

export default function BannerDescriptionsTab()
{
	const {message} = AntdApp.useApp();

	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [fields, setFields] = useState([]);
	const [descriptionDrafts, setDescriptionDrafts] = useState({});
	const [xmlTagDrafts, setXmlTagDrafts] = useState({});

	const loadFields = useCallback(async () =>
	{
		setLoading(true);
		try
		{
			const result = await listFieldRegistry();
			if (result.ok !== false)
			{
				const byKey = new Map((result.fields || []).map((f) => [f.key, f]));
				setFields(BANNER_FIELD_ORDER.map((key) => byKey.get(key)).filter(Boolean));
			}
			else
			{
				message.error(result.error || "Impossibile caricare il catalogo campi");
			}
		}
		catch (error)
		{
			message.error("Errore durante il caricamento dei campi");
		}
		finally
		{
			setLoading(false);
		}
	}, [message]);

	useEffect(() =>
	{
		loadFields();
	}, [loadFields]);

	// Campi delle gabbie banner (uguali per tutti i clienti) e quali compaiono al tasto destro
	// in pre-elaborazione.
	const [bannerFields, setBannerFields] = useState([]);
	const [bannerFieldsLoading, setBannerFieldsLoading] = useState(true);
	const [savingContextMenu, setSavingContextMenu] = useState(false);

	useEffect(() =>
	{
		let active = true;
		fetchBannerFields()
			.then((fields) => { if (active) setBannerFields(fields); })
			.catch(() => { if (active) message.error("Impossibile caricare i campi del banner"); })
			.finally(() => { if (active) setBannerFieldsLoading(false); });
		return () => { active = false; };
	}, [message]);

	const handleToggleContextMenu = useCallback(async (field, visible) =>
	{
		const nextKeys = bannerFields
			.filter((f) => (f.key === field.key ? visible : f.contextMenu))
			.map((f) => f.key);

		setSavingContextMenu(true);
		try
		{
			setBannerFields(await saveBannerContextMenuFields(nextKeys));
			message.success(visible
				? `"${field.name}" visibile al tasto destro`
				: `"${field.name}" nascosto dal tasto destro`);
		}
		catch (error)
		{
			message.error(error?.response?.data?.error || "Aggiornamento non riuscito");
		}
		finally
		{
			setSavingContextMenu(false);
		}
	}, [bannerFields, message]);

	const contextMenuColumns = useMemo(() => ([
		{
			title: "Campo",
			dataIndex: "name",
			key: "name",
			render: (_, field) => <Text strong>{field.name}</Text>
		},
		{
			title: "Visibile al tasto destro",
			dataIndex: "contextMenu",
			key: "contextMenu",
			width: 200,
			render: (_, field) => (
				<Switch
					checked={Boolean(field.contextMenu)}
					disabled={savingContextMenu}
					onChange={(checked) => handleToggleContextMenu(field, checked)}
				/>
			)
		}
	]), [savingContextMenu, handleToggleContextMenu]);

	const handleChangeXmlExportType = useCallback(async (field, value) =>
	{
		setSaving(true);
		try
		{
			const result = await updateFieldRegistry(field._id, {xmlExportType: value});
			if (result.ok !== false)
			{
				message.success(value === "attribute" ? "Esportato come attributo" : "Esportato come tag annidato");
				loadFields();
			}
			else
			{
				message.error(result.error || "Aggiornamento non riuscito");
			}
		}
		finally
		{
			setSaving(false);
		}
	}, [message, loadFields]);

	const handleSaveXmlTag = useCallback(async (field) =>
	{
		const draft = xmlTagDrafts[field.key];
		if (draft === undefined || draft === (field.xmlTag || ""))
			return;

		setSaving(true);
		try
		{
			const result = await updateFieldRegistry(field._id, {xmlTag: draft});
			if (result.ok !== false)
			{
				message.success("Tag XML aggiornato");
				setXmlTagDrafts((prev) =>
				{
					const next = {...prev};
					delete next[field.key];
					return next;
				});
				loadFields();
			}
			else
			{
				message.error(result.error || "Aggiornamento non riuscito");
			}
		}
		finally
		{
			setSaving(false);
		}
	}, [xmlTagDrafts, message, loadFields]);

	const handleSaveDescription = useCallback(async (field) =>
	{
		const draft = descriptionDrafts[field.key];
		if (draft === undefined || draft === (field.aiDescription || ""))
			return;

		setSaving(true);
		try
		{
			const result = await updateFieldRegistry(field._id, {aiDescription: draft});
			if (result.ok !== false)
			{
				message.success("Descrizione aggiornata");
				setDescriptionDrafts((prev) =>
				{
					const next = {...prev};
					delete next[field.key];
					return next;
				});
				loadFields();
			}
			else
			{
				message.error(result.error || "Aggiornamento non riuscito");
			}
		}
		finally
		{
			setSaving(false);
		}
	}, [descriptionDrafts, message, loadFields]);

	const columns = useMemo(() => ([
		{
			title: "Campo",
			dataIndex: "name",
			key: "name",
			width: 200,
			render: (_, field) => (
				<Space direction="vertical" size={2}>
					<Text strong>{field.name}</Text>
					<Text type="secondary" style={{fontSize: 12}}>{field.key}</Text>
				</Space>
			)
		},
		{
			title: "Descrizione",
			dataIndex: "aiDescription",
			key: "aiDescription",
			render: (_, field) =>
			{
				const value = descriptionDrafts[field.key] !== undefined
					? descriptionDrafts[field.key]
					: (field.aiDescription || "");
				return (
					<TextArea
						autoSize={{minRows: 1, maxRows: 4}}
						value={value}
						placeholder="Descrizione informativa del campo"
						disabled={saving}
						onChange={(e) => setDescriptionDrafts((prev) => ({...prev, [field.key]: e.target.value}))}
						onBlur={() => handleSaveDescription(field)}
					/>
				);
			}
		},
		{
			title: "Tag XML",
			dataIndex: "xmlTag",
			key: "xmlTag",
			width: 200,
			render: (_, field) =>
			{
				const value = xmlTagDrafts[field.key] !== undefined
					? xmlTagDrafts[field.key]
					: (field.xmlTag || "");
				return (
					<Input
						value={value}
						placeholder={field.key}
						disabled={saving}
						onChange={(e) => setXmlTagDrafts((prev) => ({...prev, [field.key]: e.target.value}))}
						onBlur={() => handleSaveXmlTag(field)}
						onPressEnter={(e) => e.target.blur()}
					/>
				);
			}
		},
		{
			title: "Struttura XML",
			dataIndex: "xmlExportType",
			key: "xmlExportType",
			width: 180,
			render: (_, field) => (
				<Select
					size="small"
					style={{minWidth: 170}}
					value={field.xmlExportType || "element"}
					options={XML_EXPORT_TYPE_OPTIONS}
					onChange={(value) => handleChangeXmlExportType(field, value)}
					disabled={saving}
				/>
			)
		}
	]), [descriptionDrafts, xmlTagDrafts, saving, handleSaveDescription, handleSaveXmlTag, handleChangeXmlExportType]);

	return (
		<Space direction="vertical" size={24} style={{width: "100%"}}>
			<Card title="Campi al tasto destro" className="settings-content-card card-elev">
				<Space direction="vertical" size={12} style={{width: "100%"}}>
					<Text type="secondary">
						Tutti i campi delle gabbie banner, uguali per ogni cliente. Scegli quali si
						possono compilare dal tasto destro in pre-elaborazione; nell'editor card
						restano sempre tutti.
					</Text>
					<Table
						rowKey="key"
						size="small"
						loading={bannerFieldsLoading}
						columns={contextMenuColumns}
						dataSource={bannerFields}
						pagination={false}
					/>
				</Space>
			</Card>
			<Card title="Formato XML configurabile" className="settings-content-card card-elev">
				<Space direction="vertical" size={12} style={{width: "100%"}}>
					<Text type="secondary">
						Solo questi campi del banner hanno nome del tag e struttura XML modificabili,
						più una descrizione informativa (nessuna elaborazione AI).
					</Text>
					<Table
						rowKey="key"
						loading={loading}
						columns={columns}
						dataSource={fields}
						pagination={false}
					/>
					<Text type="secondary" style={{fontSize: 12}}>
						Gli altri campi del banner escono sempre nello stesso modo: Banner e Titolo come
						attributi <Text code>banner</Text> e <Text code>titolo</Text>, Banner video come
						attributo <Text code>banner_video</Text> al posto di Banner, Descrizione e Link
						come tag <Text code>&lt;descrizione&gt;</Text> e <Text code>&lt;link&gt;</Text>.
					</Text>
				</Space>
			</Card>
		</Space>
	);
}
